'use client';
import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface MultiProductImagesProps {
  thumbnailUrls: string[];
}

const MultiProductImages: React.FC<MultiProductImagesProps> = ({
  thumbnailUrls,
}) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [selectedImage, setSelectedImage] = useState(thumbnailUrls[0]);
  const thumbnailRefs = useRef<(HTMLImageElement | null)[]>([]);

  useEffect(() => {
    setSelectedImage(thumbnailUrls[selectedImageIndex]);

    const node = thumbnailRefs.current[selectedImageIndex];
    if (node) {
      node.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
        block: 'nearest',
      });
    }
  }, [selectedImageIndex, thumbnailUrls]);

  const prevImage = () => {
    setSelectedImageIndex(prev =>
      prev === 0 ? thumbnailUrls.length - 1 : prev - 1,
    );
  };

  const nextImage = () => {
    setSelectedImageIndex(prev =>
      prev === thumbnailUrls.length - 1 ? 0 : prev + 1,
    );
  };

  return (
    <div>
      <img
        src={selectedImage}
        alt="Selected Product"
        className="object-center rounded-md border border-gray-300 mx-auto"
      />

      <div className="flex items-center justify-center gap-2 mt-4">
        <button
          onClick={prevImage}
          className="bg-white bg-opacity-70 p-2 rounded-full shadow-md">
          <ChevronLeft size={20} />
        </button>

        <div
          className={`flex-1 flex gap-2 overflow-x-auto mt-4 ${
            thumbnailUrls.length < 5 ? 'justify-center' : ''
          } [&::-webkit-scrollbar]:hidden [-ms-overflow-style:'none'] [scrollbar-width:'none']`}>
          {thumbnailUrls.map((url, i) => (
            <img
              key={i}
              ref={el => (thumbnailRefs.current[i] = el)}
              src={url}
              onClick={() => {
                setSelectedImageIndex(i);
                setSelectedImage(url);
              }}
              className={`w-[60px] md:w-[80px] h-auto object-contain rounded-md cursor-pointer border-2 ${
                selectedImage === url ? 'border-black' : 'border-gray-300'
              }`}
              alt={`Thumbnail ${i + 1}`}
            />
          ))}
        </div>

        <button
          onClick={nextImage}
          className="bg-white bg-opacity-70 p-2 rounded-full shadow-md">
          <ChevronRight size={20} />
        </button>
      </div>
    </div>
  );
};

export default MultiProductImages;
